import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { ScrollReveal } from '@/components/ScrollReveal';
import BackToTop from '@/components/BackToTop';
import StickyReservationCTA from '@/components/StickyReservationCTA';
import { 
  ChevronDown, 
  ChevronUp, 
  Mail, 
  User, 
  Building, 
  Calendar,
  Tent,
  Truck,
  Users,
  Wrench,
  Shield,
  Settings,
  MapPin,
  Phone,
  MessageSquare
} from 'lucide-react';

const FAQ = () => {
  const [openQuestions, setOpenQuestions] = useState<string[]>([]);
  
  const toggleQuestion = (questionId: string) => {
    setOpenQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId) 
        : [...prev, questionId]
    );
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const clientQuestions = [
    {
      category: 'Réservation & location',
      questions: [
        {
          id: 'reservation-comment',
          q: 'Comment réserver un vélo ?',
          a: 'La réservation se fait en ligne sur notre site. Sélectionnez votre camping ou hébergement partenaire, choisissez vos dates et le type de vélo souhaité. Vous recevrez une confirmation par email avec toutes les informations pratiques.'
        },
        {
          id: 'reservation-avance',
          q: 'Puis-je réserver avant mon arrivée au camping ?',
          a: 'Oui, nous recommandons même de réserver à l\'avance, surtout en haute saison. Cela garantit la disponibilité des vélos et vous fait gagner du temps à votre arrivée.'
        },
        {
          id: 'types-velos',
          q: 'Quels types de vélos proposez-vous ?',
          a: 'Nous proposons des vélos adultes, vélos enfants (différentes tailles), vélos électriques et accessoires selon les sites. Chaque camping partenaire dispose d\'une sélection adaptée à sa clientèle.'
        },
        {
          id: 'recuperation-velos',
          q: 'Où récupère-t-on les vélos ?',
          a: 'Les vélos sont disponibles directement dans le camping, généralement près de l\'accueil ou dans un espace dédié. L\'emplacement exact vous sera indiqué lors de votre réservation.'
        },
        {
          id: 'reservation-groupe',
          q: 'Peut-on réserver pour plusieurs personnes ?',
          a: 'Oui, vous pouvez réserver plusieurs vélos en une seule fois pour votre famille ou votre groupe. Indiquez simplement le nombre et les types de vélos souhaités lors de la réservation.'
        }
      ]
    },
    {
      category: 'Livraison',
      questions: [
        {
          id: 'livraison-domicile',
          q: 'Livrez-vous les vélos ?',
          a: 'Oui, nous proposons un service de livraison sur certaines zones géographiques. C\'est idéal si vous séjournez en location privée ou dans un hébergement non partenaire.'
        },
        {
          id: 'zones-livraison',
          q: 'Quelles zones sont concernées par la livraison ?',
          a: 'Actuellement, nous livrons sur l\'Île de Ré et à Montalivet. Nous étudions l\'extension de ce service sur d\'autres secteurs de la côte atlantique.'
        },
        {
          id: 'velos-regles',
          q: 'Les vélos sont-ils réglés à la livraison ?',
          a: 'Oui, nos équipes livrent les vélos prêts à rouler, réglés selon vos besoins. Une vérification rapide est effectuée avec vous lors de la remise.'
        }
      ]
    },
    {
      category: 'Utilisation & assistance',
      questions: [
        {
          id: 'panne-crevaison',
          q: 'Que faire en cas de panne ou de crevaison ?',
          a: 'Contactez l\'accueil du camping ou notre service client. Nos équipes interviennent rapidement pour réparer ou remplacer le vélo. Un vélo de remplacement peut être mis à disposition si nécessaire.'
        },
        {
          id: 'velos-assures',
          q: 'Les vélos sont-ils assurés ?',
          a: 'Oui, nos vélos sont couverts par notre assurance professionnelle. En cas de problème technique ou de défaillance matérielle, nous prenons en charge la réparation ou le remplacement.'
        },
        {
          id: 'vol-casse',
          q: 'Que se passe-t-il en cas de vol ou de casse ?',
          a: 'En cas de vol, prévenez immédiatement le camping et notre service client. Pour la casse, la responsabilité dépend des circonstances. Nous privilégions toujours le dialogue et la solution amiable.'
        }
      ]
    },
    {
      category: 'Vélos reconditionnés',
      questions: [
        {
          id: 'velo-reconditionne',
          q: 'Qu\'est-ce qu\'un vélo reconditionné ?',
          a: 'Il s\'agit d\'un vélo issu de notre parc professionnel, entièrement révisé par nos mécaniciens. Chaque vélo est contrôlé, réparé si nécessaire et remis en parfait état de fonctionnement.'
        },
        {
          id: 'essai-velo',
          q: 'Puis-je essayer un vélo avant d\'acheter ?',
          a: 'Oui, nous proposons des essais sur rendez-vous. Contactez-nous pour organiser un test dans nos locaux ou, selon les secteurs, à domicile.'
        },
        {
          id: 'garantie-reconditionne',
          q: 'Les vélos reconditionnés sont-ils garantis ?',
          a: 'Oui, nous garantissons la qualité de nos vélos reconditionnés. Chaque vélo est contrôlé techniquement et nous assurons un suivi après-vente.'
        }
      ]
    },
    {
      category: 'Saisonniers',
      questions: [
        {
          id: 'offre-saisonniers',
          q: 'Proposez-vous une offre pour les saisonniers ?',
          a: 'Oui, nous avons développé une offre spéciale pour les travailleurs saisonniers. Location mensuelle à partir de 30€ avec maintenance incluse et vélos reconditionnés de qualité.'
        },
        {
          id: 'tarif-mensuel',
          q: 'Quel est le tarif mensuel ?',
          a: 'Les tarifs débutent à 30€ par mois pour un vélo reconditionné avec maintenance incluse. Les prix peuvent varier selon le type de vélo et la durée de location.'
        },
        {
          id: 'entretien-inclus',
          q: 'L\'entretien est-il inclus ?',
          a: 'Oui, l\'entretien et les réparations courantes sont inclus dans l\'abonnement mensuel. Nos équipes assurent le suivi technique de votre vélo pendant toute la durée de location.'
        }
      ]
    },
    {
      category: 'Groupes',
      questions: [
        {
          id: 'offres-groupes',
          q: 'Proposez-vous des offres pour les groupes et séminaires ?',
          a: 'Oui, nous accompagnons les groupes, séminaires d\'entreprise et événements avec des solutions sur mesure. Contactez-nous en précisant le nombre de participants, les dates et le lieu pour un devis personnalisé.'
        }
      ]
    }
  ];

  const professionalQuestions = [
    {
      category: 'Mise en place du service',
      questions: [
        {
          id: 'service-fonctionnement',
          q: 'Comment fonctionne un service vélo avec Les Hirondelles ?',
          a: 'Nous installons et gérons un parc de vélos directement dans votre établissement. Nous nous occupons de tout : vélos, maintenance, réservations en ligne, service client. Vous bénéficiez du service sans la charge opérationnelle.'
        },
        {
          id: 'recruter-personnel',
          q: 'Faut-il recruter du personnel ?',
          a: 'Non, c\'est tout l\'intérêt de notre service. Nos équipes gèrent l\'exploitation, la maintenance et le service client. Votre personnel n\'a pas besoin de formation spécifique vélo.'
        },
        {
          id: 'nombre-velos-demarrer',
          q: 'Combien de vélos faut-il pour démarrer ?',
          a: 'Cela dépend de la taille de votre établissement. Nous proposons des parcs de 8 à 50+ vélos selon vos besoins. Nous dimensionnons ensemble le parc optimal pour votre clientèle.'
        }
      ]
    },
    {
      category: 'Modèles économiques',
      questions: [
        {
          id: 'modeles-economiques',
          q: 'Quels modèles économiques proposez-vous ?',
          a: 'Trois options principales : service clé en main avec vélos neufs (contrats 3 ans), location mensuelle tout compris (notre cœur de métier), ou mise à disposition sans investissement initial pour les petits établissements.'
        },
        {
          id: 'location-mensuelle-compris',
          q: 'En quoi consiste la location mensuelle tout compris ?',
          a: 'Vous louez les vélos au mois (vélos + entretien + exploitation + accompagnement). Nous gérons tout opérationnellement. Vous percevez une commission sur le chiffre d\'affaires généré sans avancer de frais.'
        },
        {
          id: 'mise-disposition-investissement',
          q: 'Que signifie la mise à disposition sans investissement ?',
          a: 'Pour les petits campings (8-20 vélos), nous installons un parc sans demander d\'avance de frais. Vous ne payez qu\'une commission sur les locations effectuées. Idéal pour tester le service.'
        }
      ]
    },
    {
      category: 'Parcs vélos & produits',
      questions: [
        {
          id: 'velos-enfants-adaptes',
          q: 'Proposez-vous des vélos enfants adaptés ?',
          a: 'Oui, nous avons développé des vélos enfants spécifiquement optimisés pour l\'usage professionnel : géométrie facilitant l\'enjambement, tailles 20" et 24", composants simplifiés pour réduire la maintenance.'
        },
        {
          id: 'velos-enfants-optimises',
          q: 'Pourquoi vos vélos enfants sont-ils optimisés ?',
          a: 'Cadres, tiges de selle et pédales en aluminium pour la légèreté. Géométrie étudiée pour l\'accessibilité. Composants choisis pour leur résistance à l\'usage intensif. Chaque détail résulte de retours terrain.'
        },
        {
          id: 'acheter-parc-velos',
          q: 'Peut-on acheter un parc de vélos ?',
          a: 'Oui, nous proposons également la vente de parcs vélos pour les établissements souhaitant exploiter en interne. Services optionnels : maintenance, révision de fin de saison, support technique.'
        }
      ]
    },
    {
      category: 'Maintenance & suivi',
      questions: [
        {
          id: 'entretien-qui',
          q: 'Qui s\'occupe de l\'entretien ?',
          a: 'Nos équipes techniques assurent la maintenance préventive et curative. Interventions régulières sur site, remplacement des pièces d\'usure, réparations. Vous n\'avez aucune charge technique.'
        },
        {
          id: 'remise-etat-fin-saison',
          q: 'Proposez-vous des remises en état de fin de saison ?',
          a: 'Oui, nous effectuons une révision complète en fin de saison : contrôle technique, remplacement des pièces usées, nettoyage, stockage hivernal. Les vélos sont prêts pour la saison suivante.'
        }
      ]
    },
    {
      category: 'Borne d\'auto-réparation',
      questions: [
        {
          id: 'borne-auto-reparation',
          q: 'Qu\'est-ce qu\'une borne d\'auto-réparation ?',
          a: 'C\'est une station équipée d\'outils et d\'une pompe permettant aux cyclistes d\'effectuer eux-mêmes les réparations de base : gonflage, serrage, ajustements simples. Service complémentaire apprécié des clients.'
        },
        {
          id: 'borne-utilite-camping',
          q: 'À quoi sert-elle pour un camping ?',
          a: 'Elle offre un service supplémentaire valorisant la mobilité douce, réduit les demandes d\'intervention pour les petits problèmes et renforce l\'image éco-responsable de votre établissement.'
        },
        {
          id: 'outils-securises',
          q: 'Les outils sont-ils sécurisés ?',
          a: 'Oui, tous les outils sont fixés à la borne par des câbles sécurisés. L\'installation résiste aux intempéries et à l\'usage intensif. Maintenance et remplacement assurés par nos équipes.'
        }
      ]
    },
    {
      category: 'Outils & accompagnement',
      questions: [
        {
          id: 'outils-gestion-contrats',
          q: 'Proposez-vous des outils de gestion et de contrats ?',
          a: 'Oui, nous intégrons le logiciel Keira pour la gestion : réservations en ligne, contrats digitalisés, suivi des stocks, reporting d\'activité. Interface simple et complète pour vos équipes.'
        },
        {
          id: 'demarrer-partenariat',
          q: 'Comment démarrer un partenariat ?',
          a: 'Contactez-nous via notre formulaire professionnel. Nous étudions ensemble vos besoins, dimensionnons la solution adaptée et vous proposons un accompagnement personnalisé pour le lancement.'
        }
      ]
    }
  ];

  const QuestionAccordion = ({ question, isOpen, onToggle }: { question: any, isOpen: boolean, onToggle: () => void }) => (
    <div className="border border-stone-200 rounded-lg overflow-hidden">
      <button
        className="w-full p-4 text-left hover:bg-stone-50 transition-colors duration-200 flex items-center justify-between"
        onClick={onToggle}
      >
        <span className="font-semibold text-slate-900 pr-4">{question.q}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-slate-600 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-600 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-stone-100">
          <p className="text-slate-700 leading-relaxed pt-3">{question.a}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* 1️⃣ EN-TÊTE DE PAGE */}
      <section className="bg-primary text-primary-foreground py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <div className="w-16 h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl font-bold text-primary-foreground">?</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Foire aux questions – Les Hirondelles
            </h1>
            <p className="text-xl lg:text-2xl text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto mb-8">
              Trouvez rapidement les réponses à vos questions selon votre situation.
            </p>
            
            {/* Boutons de navigation */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => scrollToSection('faq-clients')}
                size="lg" 
                variant="secondary" 
                className="bg-white text-primary hover:bg-white/90 px-8 py-4 text-lg font-semibold"
              >
                <User className="mr-2 h-5 w-5" />
                Je suis client
              </Button>
              <Button 
                onClick={() => scrollToSection('faq-professionnels')}
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg font-semibold"
              >
                <Building className="mr-2 h-5 w-5" />
                Je suis professionnel
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 2️⃣ BLOC 1 — FAQ CLIENTS */}
      <section id="faq-clients" className="py-20 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="h-6 w-6 text-emerald-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                FAQ – Clients, vacanciers & saisonniers
              </h2>
              <p className="text-lg text-slate-600">
                Toutes les réponses pour bien préparer votre location de vélo
              </p>
            </div>
          </ScrollReveal>

          <div className="space-y-8">
            {clientQuestions.map((category, categoryIndex) => (
              <ScrollReveal key={category.category} delay={categoryIndex * 100}>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.questions.map((question) => (
                      <QuestionAccordion
                        key={question.id}
                        question={question}
                        isOpen={openQuestions.includes(question.id)}
                        onToggle={() => toggleQuestion(question.id)}
                      />
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* CTA Clients */}
          <ScrollReveal>
            <div className="mt-12 text-center">
              <div className="bg-emerald-50 rounded-2xl p-8 border border-emerald-200">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">
                  Vous ne trouvez pas votre réponse ?
                </h3>
                <p className="text-slate-700 mb-6">
                  Notre équipe est là pour vous aider personnellement.
                </p>
                <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                  <Link to="/nous-contacter">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Contactez-nous
                  </Link>
                </Button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 3️⃣ BLOC 2 — FAQ PROFESSIONNELS */}
      <section id="faq-professionnels" className="py-20 bg-slate-50 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="h-6 w-6 text-slate-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                FAQ – Professionnels, campings & hébergements
              </h2>
              <p className="text-lg text-slate-600">
                Tout savoir sur nos solutions professionnelles et nos partenariats
              </p>
            </div>
          </ScrollReveal>

          <div className="space-y-8">
            {professionalQuestions.map((category, categoryIndex) => (
              <ScrollReveal key={category.category} delay={categoryIndex * 100}>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center">
                    <div className="w-2 h-2 bg-slate-600 rounded-full mr-3"></div>
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.questions.map((question) => (
                      <QuestionAccordion
                        key={question.id}
                        question={question}
                        isOpen={openQuestions.includes(question.id)}
                        onToggle={() => toggleQuestion(question.id)}
                      />
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* CTA Professionnels */}
          <ScrollReveal>
            <div className="mt-12 text-center">
              <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">
                  Vous êtes un établissement ?
                </h3>
                <p className="text-slate-700 mb-6">
                  Parlons de votre projet et étudions ensemble la solution adaptée à vos besoins.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg" className="bg-slate-800 hover:bg-slate-900">
                    <Link to="/professionnels">
                      <Building className="mr-2 h-4 w-4" />
                      Découvrir nos solutions
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link to="/nous-contacter">
                      <Phone className="mr-2 h-4 w-4" />
                      Nous contacter
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Boutons de navigation flottants (optionnel) */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-40">
        <Button
          onClick={() => scrollToSection('faq-clients')}
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 shadow-lg"
          title="FAQ Clients"
        >
          <User className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => scrollToSection('faq-professionnels')}
          size="sm"
          variant="outline"
          className="bg-white shadow-lg"
          title="FAQ Professionnels"
        >
          <Building className="h-4 w-4" />
        </Button>
      </div>

      <StickyReservationCTA />
      <BackToTop />
      <Footer />
    </div>
  );
};

export default FAQ;